import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";

interface Dealer {
  id: string;
  dealer_code: string;
  dealer_name: string;
  is_active: boolean;
}

interface DealerAdmin {
  id: string;
  dealer_id: string;
  is_active: boolean;
  created_at: string;
}

export default function AdminDealerAdminsPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [admins, setAdmins] = useState<DealerAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<DealerAdmin | null>(null);
  const [form, setForm] = useState({ dealerId: "", password: "", isActive: true });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const [dealersRes, adminsRes] = await Promise.all([
        supabase.from("dealers").select("id, dealer_code, dealer_name, is_active").order("dealer_name"),
        supabase.from("dealer_admins").select("id, dealer_id, is_active, created_at").order("created_at", { ascending: false }),
      ]);

      if (dealersRes.error) throw dealersRes.error;
      if (adminsRes.error) throw adminsRes.error;

      setDealers(dealersRes.data || []);
      setAdmins(adminsRes.data || []);
    } catch (e: any) {
      setError(e.message || "Veriler yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAdd = () => {
    setEditing(null);
    setForm({ dealerId: "", password: "", isActive: true });
    setShowModal(true);
  };

  const openEdit = (admin: DealerAdmin) => {
    setEditing(admin);
    setForm({ dealerId: admin.dealer_id, password: "", isActive: admin.is_active });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.dealerId || !form.password.trim()) return;
    setSaving(true);
    try {
      const supabase = getSupabase();
      if (editing) {
        const updateData: Record<string, any> = {
          dealer_id: form.dealerId,
          is_active: form.isActive,
        };
        if (form.password.trim()) {
          updateData.password_hash = form.password;
        }
        const { error: err } = await supabase
          .from("dealer_admins")
          .update(updateData)
          .eq("id", editing.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from("dealer_admins").insert({
          dealer_id: form.dealerId,
          password_hash: form.password,
          is_active: form.isActive,
        });
        if (err) throw err;
      }
      setShowModal(false);
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Kaydetme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (admin: DealerAdmin) => {
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase
        .from("dealer_admins")
        .update({ is_active: !admin.is_active })
        .eq("id", admin.id);
      if (err) throw err;
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Durum güncellenirken hata oluştu.");
    }
  };

  const deleteAdmin = async (admin: DealerAdmin) => {
    if (!confirm("Bu bayi admin tanımını silmek istediğinize emin misiniz?")) return;
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.from("dealer_admins").delete().eq("id", admin.id);
      if (err) throw err;
      await fetchData();
    } catch (e: any) {
      alert(e.message || "Silinirken hata oluştu.");
    }
  };

  const getDealerInfo = (dealerId: string) => {
    const d = dealers.find((x) => x.id === dealerId);
    return d ? `${d.dealer_name} (${d.dealer_code})` : "-";
  };

  const getDealerCode = (dealerId: string) => {
    const d = dealers.find((x) => x.id === dealerId);
    return d?.dealer_code || "-";
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="dealer-admins">Bayi Admin Tanımlamaları</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Bayilere admin yetkisi tanımlayın, şifrelerini yönetin</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> Bayi Admin Ekle
        </button>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchData} className="ml-auto text-accent-700 font-medium cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
          {/* Desktop Table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="bg-background-100 text-left">
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Kodu</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Bayi Adı</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                  <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-foreground-400 text-sm">
                      <i className="ri-shield-user-line text-3xl block mb-2"></i>
                      Henüz bayi admin tanımı yapılmamış.
                    </td>
                  </tr>
                ) : (
                  admins.map((a) => (
                    <tr key={a.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900">{getDealerCode(a.dealer_id)}</td>
                      <td className="px-4 py-3 text-foreground-900">{getDealerInfo(a.dealer_id)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(a)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${a.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                          {a.is_active ? "Aktif" : "Pasif"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(a.created_at).toLocaleDateString("tr-TR")}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openEdit(a)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                          <button onClick={() => deleteAdmin(a)} className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-background-200/70">
            {admins.length === 0 ? (
              <div className="px-4 py-10 text-center text-foreground-400 text-sm">
                <i className="ri-shield-user-line text-3xl block mb-2"></i>
                Henüz bayi admin tanımı yapılmamış.
              </div>
            ) : (
              admins.map((a) => (
                <div key={a.id} className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-foreground-900 text-sm">{getDealerInfo(a.dealer_id)}</p>
                      <p className="text-xs text-foreground-400">{new Date(a.created_at).toLocaleDateString("tr-TR")}</p>
                    </div>
                    <button onClick={() => toggleActive(a)} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${a.is_active ? "bg-primary-100 text-primary-700" : "bg-foreground-100 text-foreground-500"}`}>
                      {a.is_active ? "Aktif" : "Pasif"}
                    </button>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-2">
                    <button onClick={() => openEdit(a)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer active:scale-[0.98]"><i className="ri-edit-line text-sm"></i></button>
                    <button onClick={() => deleteAdmin(a)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-500 hover:bg-accent-100 cursor-pointer active:scale-[0.98]"><i className="ri-delete-bin-line text-sm"></i></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold text-foreground-950"><a id="dealer-admin-form">{editing ? "Bayi Admin Düzenle" : "Yeni Bayi Admin"}</a></h4>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Bayi *</label>
                <select value={form.dealerId} onChange={(e) => setForm({ ...form, dealerId: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400 cursor-pointer">
                  <option value="">Seçiniz</option>
                  {dealers.filter((d) => d.is_active).map((d) => (
                    <option key={d.id} value={d.id}>{d.dealer_name} ({d.dealer_code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">
                  {editing ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Bayi Admin Şifresi *"}
                </label>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-accent-400" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="da-active" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="w-4 h-4 rounded border-background-200/70 text-accent-500 focus:ring-accent-400" />
                <label htmlFor="da-active" className="text-sm text-foreground-700">Aktif</label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={save} disabled={saving || !form.dealerId || (!editing && !form.password.trim())} className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}